'use client';

import { useState } from 'react';
import {
  Factory,
  ClipboardList,
  Package,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Play,
  TrendingUp,
  ArrowRight,
  ListOrdered,
} from 'lucide-react';
import { getManufacturingStats, workOrders, boms, getTodayOperations } from '../data/mock-data';
import { workOrderStatusConfig, workOrderPriorityConfig } from '../types';
import type { ManufacturingPage } from '../index';

type TabType = 'workOrders' | 'bom';

interface ManufacturingDashboardProps {
  onNavigate: (page: ManufacturingPage) => void;
}

export function ManufacturingDashboard({ onNavigate }: ManufacturingDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>('workOrders');
  const stats = getManufacturingStats();
  const todayOperations = getTodayOperations();

  // 최근 작업지시
  const recentWorkOrders = [...workOrders]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  // 긴급 작업지시
  const urgentWorkOrders = workOrders.filter(
    (wo) => wo.priority === 'urgent' && wo.status !== 'completed' && wo.status !== 'cancelled'
  );

  return (
    <div className="space-y-6">
      {/* 요약 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-muted-foreground truncate">전체 작업지시</div>
              <div className="text-xl font-bold mt-1">{stats.totalWorkOrders}건</div>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <ClipboardList className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-muted-foreground truncate">진행중</div>
              <div className="text-xl font-bold mt-1">{stats.inProgressOrders}건</div>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <Play className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-muted-foreground truncate">완료</div>
              <div className="text-xl font-bold mt-1">{stats.completedOrders}건</div>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-muted-foreground truncate">생산 달성률</div>
              <div className="text-xl font-bold mt-1">{stats.completionRate.toFixed(1)}%</div>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {/* 생산 현황 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <Factory className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-sm text-muted-foreground">활성 BOM</span>
          </div>
          <div className="text-xl font-bold">{stats.activeBOMs}개</div>
          <div className="text-xs text-muted-foreground mt-1">제품 구성표</div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <Package className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-sm text-muted-foreground">계획 수량</span>
          </div>
          <div className="text-xl font-bold">{stats.totalPlannedQty.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground mt-1">전체 작업지시</div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-sm text-muted-foreground">생산 수량</span>
          </div>
          <div className="text-xl font-bold">{stats.totalProducedQty.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground mt-1">완료된 수량</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 진행중인 공정 */}
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Play className="w-4 h-4" />
              진행중인 공정
            </h3>
          </div>
          {todayOperations.length > 0 ? (
            <div className="space-y-3">
              {todayOperations.map((op, index) => (
                <div
                  key={index}
                  className="p-3 rounded-lg bg-accent/50 border border-border"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">{op.workOrderCode}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                      진행중
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground mb-2">{op.itemName}</div>
                  <div className="flex items-center justify-between text-xs">
                    <span>{op.operationName} @ {op.workstation}</span>
                    <span className="font-medium">
                      {op.completedQty} / {op.totalQty}
                    </span>
                  </div>
                  <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-500 rounded-full"
                      style={{ width: `${(op.completedQty / op.totalQty) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              현재 진행중인 공정이 없습니다.
            </div>
          )}
        </div>

        {/* 긴급 작업지시 */}
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              긴급 작업지시
            </h3>
            <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {urgentWorkOrders.length}건
            </span>
          </div>
          {urgentWorkOrders.length > 0 ? (
            <div className="space-y-3">
              {urgentWorkOrders.map((wo) => {
                const statusConfig = workOrderStatusConfig[wo.status];
                const statusColors: Record<string, string> = {
                  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
                  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                  yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
                  green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
                  red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                };
                return (
                  <div
                    key={wo.id}
                    className="p-3 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{wo.code}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[statusConfig.color]}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                    <div className="text-sm">{wo.itemName}</div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
                      <span>수량: {wo.qty.toLocaleString()}</span>
                      <span>납기: {wo.plannedEndDate}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              긴급 작업지시가 없습니다.
            </div>
          )}
        </div>
      </div>

      {/* 탭 형태 테이블 섹션 */}
      <div className="bg-card border border-border rounded-lg">
        {/* 탭 헤더 */}
        <div className="flex items-center justify-between border-b border-border">
          <div className="flex">
            <button
              onClick={() => setActiveTab('workOrders')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'workOrders'
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <ListOrdered className="w-4 h-4" />
              최근 작업지시
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
                activeTab === 'workOrders'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>{recentWorkOrders.length}</span>
            </button>
            <button
              onClick={() => setActiveTab('bom')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'bom'
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <Factory className="w-4 h-4" />
              BOM 목록
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
                activeTab === 'bom'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>{boms.slice(0, 4).length}</span>
            </button>
          </div>
          <button
            onClick={() => onNavigate(activeTab === 'workOrders' ? 'work-orders' : 'bom')}
            className="text-sm text-primary hover:underline flex items-center gap-1 pr-4"
          >
            전체보기 <ArrowRight className="w-3 h-3" />
          </button>
        </div>

        {/* 탭 컨텐츠 */}
        <div className="p-4">
          {activeTab === 'workOrders' ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 font-medium text-muted-foreground w-[50%]">품목</th>
                    <th className="text-right py-2 font-medium text-muted-foreground w-[25%]">수량</th>
                    <th className="text-right py-2 font-medium text-muted-foreground w-[25%]">상태</th>
                  </tr>
                </thead>
                <tbody>
                  {recentWorkOrders.map((wo) => {
                    const statusConfig = workOrderStatusConfig[wo.status];
                    const statusColors: Record<string, string> = {
                      gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
                      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                      yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
                      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                      orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
                      red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                    };
                    return (
                      <tr key={wo.id} className="border-b border-border/50 hover:bg-accent/50">
                        <td className="py-2">
                          <div className="truncate">{wo.itemName}</div>
                          {wo.priority === 'urgent' && (
                            <span className="text-xs text-red-500">긴급</span>
                          )}
                        </td>
                        <td className="py-2 text-right whitespace-nowrap">
                          {wo.producedQty} / {wo.qty}
                        </td>
                        <td className="py-2 text-right">
                          <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${statusColors[statusConfig.color]}`}>
                            {statusConfig.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="space-y-3">
              {boms.slice(0, 4).map((bom) => (
                <div
                  key={bom.id}
                  className="p-3 rounded-lg bg-accent/50 border border-border"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">{bom.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      bom.status === 'active'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : bom.status === 'draft'
                        ? 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {bom.status === 'active' ? '활성' : bom.status === 'draft' ? '초안' : '비활성'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{bom.code}</span>
                    <span>₩{bom.totalCost.toLocaleString()}/개</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 빠른 작업 */}
      <div className="p-4 rounded-lg bg-card border border-border">
        <h3 className="font-semibold mb-4">빠른 작업</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onClick={() => onNavigate('work-orders')}
            className="p-3 rounded-lg bg-accent hover:bg-accent/80 text-left transition-colors"
          >
            <ClipboardList className="w-5 h-5 mb-2 text-primary" />
            <div className="text-sm font-medium">작업지시 등록</div>
          </button>
          <button
            onClick={() => onNavigate('bom')}
            className="p-3 rounded-lg bg-accent hover:bg-accent/80 text-left transition-colors"
          >
            <Factory className="w-5 h-5 mb-2 text-primary" />
            <div className="text-sm font-medium">BOM 등록</div>
          </button>
          <button
            onClick={() => onNavigate('workstations')}
            className="p-3 rounded-lg bg-accent hover:bg-accent/80 text-left transition-colors"
          >
            <Clock className="w-5 h-5 mb-2 text-primary" />
            <div className="text-sm font-medium">작업장 관리</div>
          </button>
          <button
            onClick={() => onNavigate('stock-entry')}
            className="p-3 rounded-lg bg-accent hover:bg-accent/80 text-left transition-colors"
          >
            <Package className="w-5 h-5 mb-2 text-primary" />
            <div className="text-sm font-medium">생산 입고</div>
          </button>
        </div>
      </div>
    </div>
  );
}
